<template>
  <BaseModalEditCreate
    :mode="mode === 'view' ? 'edit' : mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div>
        <div class="mt-4" :class="{ 'pointer-events-none': viewOnly }">
          <label for="remoteCode" class="text-sm font-medium">
            {{ $t('representative.traderSiteRemoteCode') }}
          </label>
          <BaseInput
            v-model="modifiedData.traderSiteRemotedCode"
            color="base"
            show-error-as-tooltip
            :error="
              $v.traderSiteRemotedCode.required.$invalid
                ? $v.traderSiteRemotedCode.required.$message
                : ''
            " />
        </div>

        <div class="mt-4">
          <label class="text-sm font-medium">{{ $t('representative.customer') }}</label>
          <div v-if="viewOnly" class="mt-1 text-sm">
            {{ selectedCustomerItem?.displayName || '-' }}
          </div>
          <BaseSearchDropdown
            v-else
            v-model="selectedCustomerItem"
            :list-items="customerItems"
            :count="customerItems.length"
            :noResultsText="$t('messages.noResultsText')"
            show-error-as-tooltip
            :error="$v.customerId.required.$invalid ? $v.customerId.required.$message : ''"
            @clear-selection="handleCustomerSelection"
            @updateSearch="updateCustomerSearch"
            @item-selected="handleCustomerSelection"></BaseSearchDropdown>
        </div>
      </div>

      <div :class="{ 'pointer-events-none': viewOnly }">
        <label for="traderSite" class="text-sm font-medium">{{
          $t('representative.traderSite')
        }}</label>
        <BaseSearchDropdown
          :list-items="traderSiteItems"
          v-model="selectedTraderSiteItem"
          :no-results-text="$t('messages.noResultsText')"
          name="traderSite"
          show-error-as-tooltip
          internal-filter
          :disabled="viewOnly || !selectedCustomer"
          :error="$v.traderSiteId.required.$invalid ? $v.traderSiteId.required.$message : ''"
          @item-selected="handleTraderSiteSelection"
          @clear-selection="handleTraderSiteSelection" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import type { DCTraderSite, DCCustomer } from '@/types/DataCollection';
import type { TraderSite } from '@/types/Traders';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import { deepCopy } from '@/utils/data';
import { RecordMode } from '@/types/Base';
import { getDCCustomersWithConditions } from '@/services/api/dataCollection';
import { getTraderSitesWithConditions } from '@/services/api/traders';

const { t: $t } = useI18n();

const props = defineProps<{
  traderSite: DCTraderSite;
  mode: 'add' | 'edit' | 'view';
  viewOnly?: boolean;
  representativeId: string;
}>();

const emit = defineEmits<{
  save: [traderSite: DCTraderSite];
  closeModal: [];
}>();

const modifiedData = ref<DCTraderSite>(deepCopy(props.traderSite));
const originalData = ref<DCTraderSite>(deepCopy(props.traderSite));

const customers = ref<DCCustomer[]>([]);
const allCustomers = ref<DCCustomer[]>([]);
const traderSites = ref<TraderSite[]>([]);

const selectedCustomer = ref<DCCustomer>();
const selectedTraderSite = ref<Partial<TraderSite>>();

const selectedCustomerItem = computed(() => {
  if (!selectedCustomer.value) return null;
  return (
    customerItems.value.find(
      (item) => item.value === (selectedCustomer.value?.customerId || selectedCustomer.value?.id)
    ) || null
  );
});

const selectedTraderSiteItem = computed(() => {
  if (!selectedTraderSite.value) return null;
  return traderSiteItems.value.find((item) => item.value === selectedTraderSite.value?.id) || null;
});

const isModified = computed(() => {
  return JSON.stringify(modifiedData.value) !== JSON.stringify(originalData.value);
});

const customerItems = computed(() => {
  return customers.value.map((customer) => ({
    value: customer.customerId || customer.id || '',
    name: customer.customer?.trader?.name || '',
    displayName: `${customer.customer?.code} - ${customer.customer?.trader?.name}` || '',
  }));
});

const traderSiteItems = computed(() => {
  return traderSites.value.map((site) => ({
    value: site.id || '',
    name: site.name || '',
    displayName: site.code ? `${site.name} (${site.code})` : site.name || '',
    item: site,
  }));
});

const rules = {
  customerId: { required },
  traderSiteId: { required },
  traderSiteRemotedCode: { required },
};

const $v = useVuelidate(rules, {
  customerId: computed(
    () => selectedCustomer.value?.customerId || selectedCustomer.value?.id || ''
  ),
  traderSiteId: computed(() => selectedTraderSite.value?.id || ''),
  traderSiteRemotedCode: computed(() => modifiedData.value.traderSiteRemotedCode),
});

onMounted(async () => {
  await loadCustomers();

  // In edit or view mode, preselect the customer and trader site
  if ((props.mode === 'edit' || props.mode === 'view') && modifiedData.value.traderSiteId) {
    // Find the customer that owns this trader site
    // We need to load trader sites for all customers to find which one has this trader site
    const traderId = modifiedData.value.traderSite?.traderId;
    if (traderId) {
      const customer = customers.value.find(
        (c) => c.customer?.trader?.id?.toUpperCase() === traderId.toUpperCase()
      );
      if (customer) {
        selectedCustomer.value = customer;
        await loadTraderSitesForCustomer(traderId);
        const traderSite = traderSites.value.find(
          (ts) => ts.id === modifiedData.value.traderSiteId
        );
        if (traderSite) selectedTraderSite.value = traderSite;
      }
    }
  }
});

async function loadCustomers() {
  const res = await getDCCustomersWithConditions({ representativeId: props.representativeId });
  if (res) {
    allCustomers.value = res.data;
    customers.value = res.data;
  }
}

async function loadTraderSitesForCustomer(traderId?: string) {
  const traderIdToUse = traderId || selectedCustomer.value?.customer?.traderId;
  if (!traderIdToUse) return;

  const result = await getTraderSitesWithConditions({
    page: 1,
    itemsPerPage: 0,
    query: '',
    traderId: traderIdToUse,
    isActive: true,
  });
  if (result) {
    traderSites.value = result.data;
  }
}

function handleCustomerSelection(item: any) {
  if (item && item.value) {
    const customer = customers.value.find((c) => (c.customerId || c.id) === item.value);
    if (customer) {
      selectedCustomer.value = customer;
      selectedTraderSite.value = undefined;
      traderSites.value = [];
      loadTraderSitesForCustomer(customer.customer?.traderId);
    }
  } else {
    selectedCustomer.value = undefined;
    selectedTraderSite.value = undefined;
    traderSites.value = [];
  }
}

function handleTraderSiteSelection(item: any) {
  if (item && item.item) {
    selectedTraderSite.value = item.item as TraderSite;
    modifiedData.value.traderSiteId = item.item.id;
    modifiedData.value.traderSite = item.item;
  } else {
    selectedTraderSite.value = undefined;
    modifiedData.value.traderSiteId = '';
    modifiedData.value.traderSite = {};
  }
}

async function onSave() {
  const isValid = await $v.value.$validate();
  if (!isValid) return;

  if (props.mode === 'add') {
    modifiedData.value.recordMode = RecordMode.Added;
  } else if (props.mode === 'edit') {
    modifiedData.value.recordMode = RecordMode.Modified;
  }

  emit('save', modifiedData.value);
}

function onDismissChanges() {
  emit('closeModal');
}

async function updateCustomerSearch(searchInput: string) {
  // Load all customers once if not already loaded
  if (allCustomers.value.length === 0) {
    const result = await getDCCustomersWithConditions({ representativeId: props.representativeId });
    if (!result) return;
    allCustomers.value = result.data;
  }

  // Filter locally instead of making repeated API calls
  if (!searchInput) {
    customers.value = allCustomers.value;
  } else {
    customers.value = allCustomers.value.filter(
      (x) =>
        x.customer?.code?.toLowerCase().includes(searchInput.toLowerCase()) ||
        x.customer?.trader?.name?.toLowerCase().includes(searchInput.toLowerCase())
    );
  }
}

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.traderSite')}`;
}
</script>
